import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface ComplianceRow {
  readonly brandComplianceId: string;
  readonly complianceCode: string;
  readonly assetId: string;
  readonly guidelineId: string | null;
  readonly validationType: string;
  readonly overallResult: string;
  readonly logoCompliant: boolean | null;
  readonly colorCompliant: boolean | null;
  readonly fontCompliant: boolean | null;
}

const RESULTS = ['', 'PENDING', 'PASS', 'FAIL', 'WAIVED'];
const VALIDATION_TYPES = ['', 'MANUAL', 'RULE_BASED'];

function flag(v: boolean | null, t: (key: string) => string): string {
  if (v === null) return '—';
  return v ? t('m36.common.yes') : t('m36.common.no');
}

/**
 * M36 — Brand Compliance Dashboard (§31). Register of brand-compliance
 * validations (T492) across the manual/rule-based validation types, showing
 * the overall PASS/FAIL/PENDING/WAIVED result and the per-dimension checks
 * (logo, colour, font). A FAIL result blocks publication of the asset (BR-03).
 * Filterable by overall result and validation type. Tenant isolation is
 * enforced server-side (LAW 4).
 */
export function BrandComplianceDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ComplianceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [overallResult, setOverallResult] = useState('');
  const [validationType, setValidationType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (overallResult) params.set('overallResult', overallResult);
    if (validationType) params.set('validationType', validationType);
    const qs = params.toString();
    apiRequest<{ data: ComplianceRow[]; total: number }>(`/v1/dam/compliance${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, overallResult, validationType]);

  return (
    <main aria-label="brand-compliance-dashboard">
      <h1>{t('m36.brandCompliance.title')}</h1>
      <div className="filters">
        <label>
          {t('m36.common.result')}
          <select aria-label="filter-result" value={overallResult} onChange={(e) => setOverallResult(e.target.value)}>
            {RESULTS.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.brandCompliance.validation')}
          <select aria-label="filter-validation" value={validationType} onChange={(e) => setValidationType(e.target.value)}>
            {VALIDATION_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="compliance-count">{t('m36.brandCompliance.count', { count: total })}</p>
          <DataTable<ComplianceRow>
            ariaLabel="compliance-table"
            testId="compliance-table"
            rowKey={(r) => r.brandComplianceId}
            rowDataAttrs={(r) => ({ 'data-result': r.overallResult })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m36.common.code'), render: (r) => r.complianceCode },
              { key: 'asset', header: t('m36.common.asset'), render: (r) => r.assetId },
              { key: 'validation', header: t('m36.brandCompliance.validation'), render: (r) => r.validationType },
              { key: 'result', header: t('m36.common.result'), render: (r) => r.overallResult, cellAriaLabel: (r) => `result-${r.brandComplianceId}` },
              { key: 'logo', header: t('m36.brandCompliance.col.logo'), render: (r) => flag(r.logoCompliant, t) },
              { key: 'colour', header: t('m36.brandCompliance.col.colour'), render: (r) => flag(r.colorCompliant, t) },
              { key: 'font', header: t('m36.brandCompliance.col.font'), render: (r) => flag(r.fontCompliant, t) },
            ]}
          />
        </>
      )}
    </main>
  );
}
